import { BybAnimatedBorder, BybConditionalWrapper } from "@Components/common/byb-ds";
import { useReplayDiscovery } from "@Context/game/activity/replay-discovery-context";
import { ChevronButton, ScrollContainer } from "@Components/game/session-results/results-bar/styled-components";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import { BootcampChallengeStep } from "@Constants/bootcamp";
import ScenarioResultSmall from "@Containers/shared/cards/scenario-result-small";
import { useSessionResultsStaticContext, useSessionResultsTabContext } from "@Context/game/activity/session-results-context";
import { AnalyticsEvents, GameModes } from "@Enums/index";
import { IPlayItemResult } from "@Globals/types";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { getSessionResultsScenarioAnalytics } from "@hooks/game/activity/session/session-results-analytics";
import useExitBootcamp from "@hooks/game/bootcamp/use-exit-bootcamp";
import useI18n from "@i18n";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button, Stack, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { useResultsBar } from "./use-results-bar";

const SessionResultsBar = () => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { track } = useAnalytics();
  const { showReplayHint, dismissReplayHint } = useReplayDiscovery();
  const { sessionData, sessionResults } = useSessionResultsStaticContext();
  const { currentTab, setCurrentTab } = useSessionResultsTabContext();
  const exitBootcamp = useExitBootcamp();
  const showContinueToTraining = sessionData?.bootcampStep === BootcampChallengeStep.FINAL && sessionResults?.failed !== true && currentTab > 0;

  const playItemResults = (sessionResults?.play_item_results || []) as IPlayItemResult[];
  const isMatchPlay = sessionResults?.game_mode === GameModes.MATCH_PLAY;
  const totalResults = playItemResults.length + 1;

  const { scrollContainerRef, showLeftChevron, showRightChevron, handleScroll, scroll, handleButtonClick } = useResultsBar({
    totalResults,
    setCurrentTab,
  });

  const wrapReplayHint = (children: ReactNode) => (
    <BybAnimatedBorder
      borderRadius={`${theme.shape.borderRadius * 10}px`}
      borderWidth={2}
      color={theme.palette.dsYellow?.v300}
      sx={{ cornerShape: "round" }}
    >
      {children}
    </BybAnimatedBorder>
  );

  const handleScenarioClick = (index: number) => {
    if (index === 1 && showReplayHint) {
      dismissReplayHint();
    }

    const isTabChange = index !== currentTab;
    handleButtonClick(index);

    if (!isTabChange) {
      return;
    }

    const scenarioAnalytics = getSessionResultsScenarioAnalytics(sessionResults, index, i18n);
    if (scenarioAnalytics) {
      track(AnalyticsEvents.scenario_switched, scenarioAnalytics);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      paddingTop={theme.spacing(2.5)}
      position="fixed"
      zIndex={1000}
      top={0}
      left={0}
      right={0}
    >
      {!isMatchPlay && (
        <HapticsButtonWrapper>
          <ChevronButton onClick={() => scroll("left")} disabled={!showLeftChevron}>
            <ChevronLeftIcon sx={{ width: theme.spacing(3) }} />
          </ChevronButton>
        </HapticsButtonWrapper>
      )}

      <ScrollContainer ref={scrollContainerRef} onScroll={handleScroll}>
        {sessionResults ? (
          <>
            <HapticsButtonWrapper>
              <ScenarioResultSmall
                title={i18n("session-results.results.summary")}
                selected={currentTab === 0}
                onClick={() => handleScenarioClick(0)}
              />
            </HapticsButtonWrapper>
            {isMatchPlay ? (
              <HapticsButtonWrapper>
                <BybConditionalWrapper condition={showReplayHint} trueWrapper={wrapReplayHint}>
                  <ScenarioResultSmall
                    title={i18n("session-results.results.analysis")}
                    selected={currentTab === 1}
                    onClick={() => handleScenarioClick(1)}
                  />
                </BybConditionalWrapper>
              </HapticsButtonWrapper>
            ) : (
              playItemResults.map((_, i: number) => (
                <HapticsButtonWrapper key={i}>
                  <BybConditionalWrapper condition={showReplayHint && i === 0} trueWrapper={wrapReplayHint}>
                    <ScenarioResultSmall
                      title={i18n(`session-results.results.scenario-result-short`, { index: i + 1 })}
                      selected={currentTab === i + 1}
                      onClick={() => handleScenarioClick(i + 1)}
                    />
                  </BybConditionalWrapper>
                </HapticsButtonWrapper>
              ))
            )}
          </>
        ) : (
          <>
            <HapticsButtonWrapper>
              <ScenarioResultSmall
                title={i18n("session-results.results.summary")}
                selected={currentTab === 0}
                onClick={() => handleScenarioClick(0)}
              />
            </HapticsButtonWrapper>
            <ScenarioResultSmall loading />
            <ScenarioResultSmall loading />
            <ScenarioResultSmall loading />
          </>
        )}
      </ScrollContainer>

      {!isMatchPlay && (
        <HapticsButtonWrapper>
          <ChevronButton onClick={() => scroll("right")} disabled={!showRightChevron}>
            <ChevronRightIcon sx={{ width: theme.spacing(3) }} />
          </ChevronButton>
        </HapticsButtonWrapper>
      )}

      {showContinueToTraining && (
        <Button
          size="small"
          data-shape="pill"
          aria-label={i18n("session-results.bootcamp-complete.continue")}
          onClick={exitBootcamp}
          sx={{ position: "absolute", right: theme.spacing(4) }}
        >
          {i18n("session-results.bootcamp-complete.continue")}
        </Button>
      )}
    </Stack>
  );
};

export default SessionResultsBar;
